// components/CompletionModal.js
import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { COLORS, RADIUS, SPACING } from '../theme';

const { width } = Dimensions.get('window');

/**
 * MetricCard Component
 * Displays individual statistics in the grid.
 */
const MetricCard = ({ icon, label, value, variant }) => {
  const isGreat = variant === 'great';
  const isMistake = variant === 'mistake';

  return (
    <View style={[
      styles.card, 
      isGreat && styles.cardGreat, 
      isMistake && styles.cardMistake
    ]}>
      <View style={styles.cardHeader}>
        <Text style={[styles.cardIcon, isGreat && styles.textGreat, isMistake && styles.textMistake]}>
          {icon}
        </Text>
        <Text style={[styles.cardLabel, isGreat && styles.textGreat, isMistake && styles.textMistake]}>
          {label}
        </Text>
      </View>
      <Text style={[styles.cardValue, isGreat && styles.textGreat, isMistake && styles.textMistake]}>
        {value}
      </Text>
    </View>
  );
};

/**
 * CompletionModal Component
 * Updated to match the provided UI design with a 3x2 metrics grid and specific styling.
 */
const CompletionModal = ({ visible, metrics, onRestart, onSelectOpening, onClose }) => {
  if (!metrics) return null;

  // Format the engine evaluation to show a sign and one decimal place
  const formattedEval = (val) => {
    if (val === null || val === undefined) return '0.0';
    // Assuming evaluation is in centipawns
    const score = val / 100;
    return (score > 0 ? '+' : '') + score.toFixed(1);
  };

  // Close functionality defaults to the Select Opening action if not explicitly provided
  const handleClose = onClose || onSelectOpening;

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.overlay}>
        <View style={styles.modalBox}>
          {/* Header with Close Button */}
          <TouchableOpacity 
            style={styles.closeBtn} 
            onPress={handleClose}
            accessibilityLabel="Close completion screen"
          >
            <Text style={styles.closeBtnText}>✕</Text>
          </TouchableOpacity>

          <Text style={styles.title}>Training Complete!</Text>

          {/* 3x2 Metrics Grid */}
          <View style={styles.grid}>
            <MetricCard 
              icon="📖" 
              label="Book Moves:" 
              value={metrics.bookMovesCount || 0} 
            />
            <MetricCard 
              icon="🏆" 
              label="Great Moves:" 
              value={metrics.greatMovesCount || 0} 
              variant="great"
            />
            <MetricCard 
              icon="❗" 
              label="Mistakes:" 
              value={metrics.mistakesCount || 0} 
              variant="mistake"
            />
            <MetricCard 
              icon="💡" 
              label="Hints Used:" 
              value={metrics.hintsCount || 0} 
            />
            <MetricCard 
              icon="📊" 
              label="CP Total CPL:" 
              value={`${metrics.totalCentipawnLoss || 0} cp`} 
            />
            <MetricCard 
              icon="📈" 
              label="Final Eval:" 
              value={formattedEval(metrics.finalEngineEvaluation)} 
            />
          </View>

          {/* Action Buttons */}
          <View style={styles.btnRow}>
            <TouchableOpacity style={styles.restartBtn} onPress={onRestart}>
              <View pointerEvents="none" style={styles.restartBtnGradient} />
              <Text style={styles.restartBtnText}>Restart</Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.selectBtn} onPress={onSelectOpening}>
              <Text style={styles.selectBtnText}>Select Opening</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: SPACING.md,
  },
  modalBox: {
    width: Math.min(width * 0.9, 520),
    backgroundColor: 'rgba(18, 24, 34, 0.95)',
    borderRadius: 24,
    padding: 32,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    position: 'relative',
    ...Platform.select({
      web: { backdropFilter: 'blur(20px)' },
      default: {},
    }),
  },
  closeBtn: {
    position: 'absolute',
    top: 20,
    right: 20,
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  closeBtnText: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: 20,
    fontWeight: '300',
  },
  title: {
    color: '#FFFFFF',
    fontSize: 28,
    fontWeight: '900',
    textAlign: 'center',
    marginBottom: 28,
    letterSpacing: 0.5,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 28,
  },
  card: {
    width: '31.5%',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
    alignItems: 'center',
  },
  cardGreat: {
    borderColor: 'rgba(245, 158, 11, 0.5)',
  },
  cardMistake: {
    borderColor: 'rgba(239, 68, 68, 0.5)',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  cardIcon: {
    fontSize: 12,
    marginRight: 4,
    color: 'rgba(255, 255, 255, 0.6)',
  },
  cardLabel: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  cardValue: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: '900',
  },
  textGreat: {
    color: '#F59E0B',
  },
  textMistake: {
    color: '#EF4444',
  },
  btnRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  restartBtn: {
    width: '48%',
    height: 52,
    borderRadius: RADIUS.md,
    overflow: 'hidden',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  restartBtnGradient: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#F59E0B',
    ...Platform.select({
      web: { backgroundImage: 'linear-gradient(180deg, #FBBF24 0%, #D97706 100%)' },
      default: {},
    }),
  },
  restartBtnText: {
    color: '#121822',
    fontSize: 15,
    fontWeight: '900',
    zIndex: 1,
  },
  selectBtn: {
    width: '48%',
    height: 52,
    borderRadius: RADIUS.md,
    borderWidth: 1,
    borderColor: '#D5A02A',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  selectBtnText: {
    color: '#D5A02A',
    fontSize: 15,
    fontWeight: '900',
  },
});

export default CompletionModal;